import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const SECTION_LINKS = [
  { path: '/marketplace#browse', label: 'Browse Marketplace' },
  { path: '/agents/dashboard', label: 'My Dashboard' },
  {
    path: '/agents/quickstart',
    label: 'Quickstart',
    subLinks: [
      { to: '/agents/import', label: 'Register' },
    ],
  },
  {
    path: '/agents/docs',
    label: 'Docs',
    subLinks: [
      { to: '/agents/docs#mcp', label: 'MCP' },
      { to: '/agents/docs#api', label: 'API' },
    ],
  },
];

/**
 * Shared hamburger menu for the agent surface (/agents/*) and /marketplace.
 *
 * Pinned top-left, sitting below the marketplace back-link. Open state shows
 * every section + sub-categories at once so users can scan and discover
 * (rather than hiding sub-items behind active state).
 *
 * If `heroRef` is provided, the icon flips white-on-dark while the hero is
 * on screen, then black-on-light once scrolled past — same behavior as
 * MarketplaceBackLink so the two stay visually in sync.
 */
const NAV_STORAGE_KEY = 'agent-nav-open';

const AgentSectionNav = ({ heroRef, dark = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false,
  );
  const [open, setOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      const stored = window.localStorage.getItem(NAV_STORAGE_KEY);
      if (stored !== null) return stored === 'true';
    } catch { /* ignore */ }
    // Closed by default everywhere (Nina 08-18; was open-by-default on
    // desktop). An explicit toggle still persists via NAV_STORAGE_KEY.
    return false;
  });
  const [overDark, setOverDark] = useState(!!heroRef);
  const buttonRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile((prev) => {
        // When transitioning desktop -> mobile, collapse so the panel doesn't
        // dominate a small viewport. Going mobile -> desktop, restore from
        // stored preference (default closed, Nina 08-18).
        if (!prev && mobile) setOpen(false);
        if (prev && !mobile) {
          try {
            const stored = window.localStorage.getItem(NAV_STORAGE_KEY);
            setOpen(stored !== null ? stored === 'true' : false);
          } catch { setOpen(false); }
        }
        return mobile;
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Persist user preference (only when they explicitly toggle).
  const toggleOpen = () => {
    setOpen((prev) => {
      const next = !prev;
      try { window.localStorage.setItem(NAV_STORAGE_KEY, String(next)); } catch { /* ignore */ }
      return next;
    });
  };

  // Push page content right when menu is open (desktop only — handled in CSS)
  useEffect(() => {
    if (open) document.body.classList.add('agent-nav-open');
    else document.body.classList.remove('agent-nav-open');
    return () => document.body.classList.remove('agent-nav-open');
  }, [open]);

  useEffect(() => {
    if (!heroRef) return undefined;
    const update = () => {
      const el = heroRef.current;
      if (!el) return;
      setOverDark(el.getBoundingClientRect().bottom > 40);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [heroRef]);

  // On mobile, close after route change (acts like an overlay menu).
  // On desktop, leave it as-is — sidebar persists across navigation.
  useEffect(() => {
    if (isMobile) setOpen(false);
  }, [location.pathname, isMobile]);

  const handleNavigate = (to) => {
    if (isMobile) setOpen(false);
    if (!to.includes('#')) {
      navigate(to);
      return;
    }
    const [path, hash] = to.split('#');
    if (location.pathname === path) {
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
    navigate(to);
    setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  return (
    <>
      <style>{`
        .agent-nav-hamburger {
          transition: opacity 0.15s ease, color 0.15s ease;
          opacity: 0.85;
        }
        .agent-nav-hamburger:hover { opacity: 1; }
        .agent-nav-item {
          width: 100%;
          background: transparent;
          border: none;
          padding: 9px 18px;
          text-align: left;
          font-family: inherit;
          font-size: 0.78rem;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: #1a1a2e;
          cursor: pointer;
          transition: background 0.15s ease;
        }
        .agent-nav-item:hover { background: #f4f0fb; }
        .agent-nav-item[data-active="true"] {
          color: #7b1fa2;
          background: #f4f0fb;
        }
        .agent-nav-subitem {
          width: 100%;
          background: transparent;
          border: none;
          padding: 6px 18px 6px 36px;
          text-align: left;
          font-family: inherit;
          font-size: 0.7rem;
          font-weight: 500;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: #6b7280;
          cursor: pointer;
          transition: background 0.15s ease, color 0.15s ease;
        }
        .agent-nav-subitem:hover { background: #f4f0fb; color: #1a1a2e; }
        /* 🌙 Dark variant (marketplace dark mode): the rail is fixed chrome,
           so it sits OUTSIDE the content div's dark-mode filter and needs
           real dark colors of its own. Agent doc pages stay light (no prop). */
        .agent-nav-dark .agent-nav-item { color: #e5e7eb; }
        .agent-nav-dark .agent-nav-item:hover { background: rgba(123,31,162,0.22); }
        .agent-nav-dark .agent-nav-item[data-active="true"] {
          color: #ce93d8;
          background: rgba(123,31,162,0.22);
        }
        .agent-nav-dark .agent-nav-subitem { color: #9aa0aa; }
        .agent-nav-dark .agent-nav-subitem:hover { background: rgba(123,31,162,0.22); color: #e5e7eb; }
        @media (min-width: 768px) {
          body.agent-nav-open {
            padding-left: 240px;
            transition: padding-left 0.2s ease;
          }
          /* Desktop closed-nav: clear the fixed hamburger. Lives here (not in
             the hero's inline style) so phones keep symmetric 20px padding.
             !important because the hero sets padding inline. */
          .marketplace-hero {
            padding-left: 80px !important;
          }
          /* When sidebar is open, the body push handles spacing — drop the
             hero's hamburger-clearance left pad so margins are symmetric. */
          body.agent-nav-open .marketplace-hero {
            padding-left: 20px !important;
          }
          /* MarketplaceBackLink is fixed-positioned with inline left:24, so
             body padding does not move it. Translate past the rail; using
             transform avoids overriding the component inline left. */
          body.agent-nav-open .marketplace-back-link-fixed {
            transform: translateX(216px);
          }
        }
        body { transition: padding-left 0.2s ease; }
        .marketplace-hero { transition: padding-left 0.2s ease; }
        .marketplace-back-link-fixed { transition: transform 0.2s ease, opacity 0.2s ease; }
      `}</style>

      <button
        ref={buttonRef}
        type="button"
        onClick={toggleOpen}
        aria-label={
          open
            ? (isMobile ? 'Close menu' : 'Collapse menu')
            : 'Open menu'
        }
        title={
          open
            ? (isMobile ? 'Close menu' : 'Collapse menu')
            : 'Open menu'
        }
        aria-expanded={open}
        className="agent-nav-hamburger"
        style={{
          position: 'fixed',
          top: 50,
          left: 22,
          background: 'transparent',
          color: open
            ? (dark ? '#ce93d8' : '#7b1fa2')
            // dark pages keep the closed hamburger white even past the hero —
            // near-black on the dark-mode content was invisible.
            : ((overDark || dark) ? '#fff' : '#1a1a2e'),
          textShadow: open
            ? 'none'
            : ((overDark || dark)
                ? '0 0 10px rgba(0,0,0,0.55), 0 0 18px rgba(0,0,0,0.35)'
                : '0 0 10px rgba(255,255,255,0.85), 0 0 18px rgba(255,255,255,0.6)'),
          border: 'none',
          fontSize: isMobile ? '1.3rem' : '1.6rem',
          fontWeight: 400,
          cursor: 'pointer',
          zIndex: 1003,
          padding: 4,
          lineHeight: 1,
          fontFamily: 'inherit',
        }}
      >
        {open ? (isMobile ? '×' : '«') : '☰'}
      </button>

      {open && (
        <>
          {isMobile && (
            <div
              onClick={() => setOpen(false)}
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 999,
                background: 'transparent',
              }}
            />
          )}
          <nav
            aria-label="Agent section"
            className={dark ? 'agent-nav-dark' : undefined}
            style={{
              position: 'fixed',
              top: isMobile ? 92 : 0,
              left: isMobile ? 18 : 0,
              bottom: isMobile ? undefined : 0,
              zIndex: 1002,
              background: dark ? '#141218' : '#fff',
              border: isMobile ? `1px solid ${dark ? '#2a2433' : '#e5e7eb'}` : 'none',
              borderRight: `1px solid ${dark ? '#2a2433' : '#e5e7eb'}`,
              borderRadius: isMobile ? 12 : 0,
              boxShadow: isMobile
                ? '0 12px 32px rgba(0,0,0,0.14)'
                : '2px 0 12px rgba(0,0,0,0.06)',
              padding: isMobile ? '8px 0' : '92px 0 16px',
              width: isMobile ? 'min(240px, 85vw)' : 240,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {SECTION_LINKS.map((link) => {
              const active = location.pathname === link.path.split('#')[0];
              return (
                <React.Fragment key={link.path}>
                  <button
                    type="button"
                    onClick={() => handleNavigate(link.path)}
                    className="agent-nav-item"
                    data-active={active}
                  >
                    {link.label}
                  </button>
                  {link.subLinks &&
                    link.subLinks.map((sub) => (
                      <button
                        key={sub.to}
                        type="button"
                        onClick={() => handleNavigate(sub.to)}
                        className="agent-nav-subitem"
                      >
                        {sub.label}
                      </button>
                    ))}
                </React.Fragment>
              );
            })}
          </nav>
        </>
      )}
    </>
  );
};

export default AgentSectionNav;
